import { ActivatedRouteSnapshot, CanMatchFn, ResolveFn, Route, Router, Routes } from '@angular/router';
import { PageTypeEnum } from '@api/models';
import {
  AccountCustomerInfoComponent,
} from './pages/account-page/components/account-customer-info/account-customer-info.component';
import { AccountOrdersComponent } from './pages/account-page/components/account-orders/account-orders.component';
import { AccountDiscountComponent } from './pages/account-page/components/account-discount/account-discount.component';
import { BLOG_ROUTE_PREFIX, RU_ROUTE_PREFIX, UA_ROUTE_PREFIX } from './constants';
import { inject, RESPONSE_INIT } from '@angular/core';
import { VanillaRoutingService } from './services/vanilla-routing.service';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { PageRegistryService } from './services/page-registry.service';
import { REUSE_ROUTE_DATA_KEY } from './route-reuse.strategy';

function createDynamicPageCanMatch(pageType: PageTypeEnum): CanMatchFn {
  return (_route, segments) => {
    const slug = segments[0]?.path;
    if (!slug) return false;

    return inject(PageRegistryService).getPageType(slug) === pageType;
  };
}

function createRedirectRoute(routePrefix: string): Route {
  return {
    path: ':slug',
    // never rendered: the guard always answers with a redirect, a route just needs a body
    children: [],
    canMatch: [(_route, segments) => {
      const slug = segments[0]?.path;
      const pageRegistry = inject(PageRegistryService);
      const target = slug && pageRegistry.getRedirectSlug(slug);
      if (!target) {
        return false;
      }

      const res = inject(RESPONSE_INIT, { optional: true });
      if (res) {
        res.status = 301; // permanent, so crawlers move to the new url
      }

      const router = inject(Router);
      const redirectTree = router.createUrlTree(routePrefix ? [routePrefix, target] : [target]);

      // The target has to be known before the redirect navigation starts, otherwise it falls to the
      // not-found page, which sets 404 on the very response that is supposed to be a redirect
      return pageRegistry.fetchAndRegisterPage$(target).pipe(
        map(() => redirectTree),
        catchError(() => of(redirectTree)),
      );
    }],
  };
}

const slugResolver: ResolveFn<string> = (route: ActivatedRouteSnapshot) => route.params['slug'];

export const STATIC_ROUTES: Routes = [
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () => import('./pages/index-page/index-page.component').then(c => c.IndexPageComponent),
  },
  {
    path: 'account',
    loadComponent: () => import('./pages/account-page/account-page.component').then(c => c.AccountPageComponent),
    children: [
      { path: '', pathMatch: 'full', component: AccountCustomerInfoComponent, data: { label: 'global.contacts' } },
      { path: 'orders', component: AccountOrdersComponent, data: { label: 'global.my_orders' } },
      // { path: 'reviews', component: AccountReviewsComponent, data: { label: 'account_reviews.my_reviews' } },
      { path: 'discount', component: AccountDiscountComponent, data: { label: 'global.my_discounts' } },
    ],
  },
  {
    path: 'login',
    loadComponent: () => import('./pages/login-page/login-page.component').then(c => c.LoginPageComponent),
  },
  {
    path: 'wishlist',
    loadComponent: () => import('./pages/wishlist-page/wishlist-page.component').then(c => c.WishlistPageComponent),
  },
  {
    path: 'checkout',
    loadComponent: () => import('./pages/checkout-page/checkout-page.component').then(c => c.CheckoutPageComponent),
  },
  {
    path: 'order-success',
    loadComponent: () => import('./pages/order-success-page/order-success-page.component').then(c => c.OrderSuccessPageComponent),
  },
  {
    path: 'cart',
    loadComponent: () => import('./pages/cart-page/cart-page.component').then(c => c.CartPageComponent),
  },
  {
    path: 'oauth-success',
    loadComponent: () => import('./pages/oauth-success-page/oauth-success-page.component').then(c => c.OauthSuccessPageComponent),
  },
  {
    path: 'otzyvy',
    loadComponent: () => import('./pages/store-reviews-page/store-reviews-page.component').then(c => c.StoreReviewsPageComponent),
  },
  {
    path: 'search',
    loadComponent: () => import('./pages/search-page/search-page.component').then(c => c.SearchPageComponent),
    data: { [REUSE_ROUTE_DATA_KEY]: true },
  },
  {
    path: 'order-payment-details',
    loadComponent: () => import('./pages/order-payment-details-page/order-payment-details-page.component').then(c => c.OrderPaymentDetailsPageComponent),
  },
];

const BLOG_ROUTE: Route = {
  path: BLOG_ROUTE_PREFIX,
  children: [
    {
      path: '',
      pathMatch: 'full',
      loadComponent: () => import('./pages/blog/blog-page/blog-page.component').then(c => c.BlogPageComponent),
    },
    {
      path: ':slug',
      canMatch: [createDynamicPageCanMatch(PageTypeEnum.BlogCategory)],
      loadComponent: () => import('./pages/blog/blog-category-page/blog-category-page.component').then(c => c.BlogCategoryPageComponent),
      resolve: { slug: slugResolver },
    },
    {
      path: ':slug',
      canMatch: [createDynamicPageCanMatch(PageTypeEnum.BlogPost)],
      loadComponent: () => import('./pages/blog/blog-post-page/blog-post-page.component').then(c => c.BlogPostPageComponent),
      resolve: { slug: slugResolver },
    },
  ],
};

const DYNAMIC_ROUTES: Routes = [
  {
    path: ':slug',
    canMatch: [createDynamicPageCanMatch(PageTypeEnum.Category)],
    loadComponent: () => import('./pages/category-page/category-page.component').then(c => c.CategoryPageComponent),
    resolve: { slug: slugResolver },
    data: { [REUSE_ROUTE_DATA_KEY]: true },
  },
  {
    path: ':slug',
    canMatch: [createDynamicPageCanMatch(PageTypeEnum.Product)],
    loadComponent: () => import('./pages/product-page/product-page.component').then(c => c.ProductPageComponent),
    resolve: { slug: slugResolver },
    data: { [REUSE_ROUTE_DATA_KEY]: true },
  },
  {
    path: ':slug',
    canMatch: [createDynamicPageCanMatch(PageTypeEnum.InfoPage)],
    loadComponent: () => import('./pages/info-page/info-page.component').then(c => c.InfoPageComponent),
    resolve: { slug: slugResolver },
    data: { [REUSE_ROUTE_DATA_KEY]: true },
  },
];

const NOT_FOUND_ROUTE: Route = {
  path: '**',
  loadComponent: () => import('./pages/not-found-page/not-found-page.component').then(c => c.NotFoundPageComponent),
  canActivate: [() => !inject(VanillaRoutingService).isVanillaRoutingInProgress],
};

// the redirect goes before DYNAMIC_ROUTES: a redirect keeps the type of the page it replaced
const buildChildrenRoutes = (routePrefix: string): Routes =>
  [...STATIC_ROUTES, BLOG_ROUTE, createRedirectRoute(routePrefix), ...DYNAMIC_ROUTES];

export const ALL_ROUTES: Routes = [
  { path: RU_ROUTE_PREFIX, children: buildChildrenRoutes(RU_ROUTE_PREFIX) },
  { path: UA_ROUTE_PREFIX, children: buildChildrenRoutes(UA_ROUTE_PREFIX) },
  NOT_FOUND_ROUTE,
];
